import { Box, Flex, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import type { ProgramWithStats } from '@fluenceur/types';
import { useAuth } from '@/contexts/auth-context';
import ProgramCardActions from './ProgramCardActions';
import ProgramCardAvatars from './ProgramCardAvatars';
import ProgramCardStatusBadge from './ProgramCardStatusBadge';
import ProgramCommissionPill from './ProgramCommissionPill';
import ProgramStatGrid from './ProgramStatGrid';
import { browseColors } from './browse-tokens';

interface ProgramCardProps {
  program: ProgramWithStats;
}

/**
 * A single program tile. The card body navigates to the program overview; the
 * footer exposes the accessible, labelled controls (invite / open).
 * The affiliate avatar pile reads `affiliatePreview` straight from the list
 * payload (no per-card fetch) and hides itself when the field is absent.
 */
const ProgramCard = ({ program }: ProgramCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currency = user?.currentBrand?.currency || 'EUR';

  const openDetail = () => navigate(`/programs/${program.id}/overview`);
  const openInvite = () => navigate(`/programs/${program.id}/invitation`);

  return (
    <Box
      onClick={openDetail}
      cursor="pointer"
      display="flex"
      flexDirection="column"
      bg={browseColors.cardBg}
      border="1px solid"
      borderColor={browseColors.cardBorder}
      borderRadius="18px"
      p="20px 20px 14px"
      transition="border-color .12s"
      _hover={{ borderColor: browseColors.cardBorderHover }}
    >
      <Flex align="center">
        <ProgramCardStatusBadge status={program.status} pausedByPlan={!!program.pausedByPlanAt} />
        <ProgramCardAvatars
          preview={program.affiliatePreview}
          totalCount={program.affiliatesCount ?? 0}
        />
      </Flex>

      <Text mt="12px" fontSize="16px" fontWeight={600} color={browseColors.heading} lineClamp={1}>
        {program.name}
      </Text>

      <Text
        mt="4px"
        minH="42px"
        fontSize="13px"
        lineHeight="1.6"
        color={browseColors.bodyText}
        lineClamp={2}
      >
        {program.description ?? ''}
      </Text>

      <ProgramCommissionPill commissionSettings={program.commissionSettings} currency={currency} />

      <ProgramStatGrid program={program} currency={currency} />

      <ProgramCardActions onInvite={openInvite} onOpenDetail={openDetail} />
    </Box>
  );
};

export default ProgramCard;
